// Pure decision logic for the terminal-poll watchdog (see
// terminalPollWatchdog.ts for the React/DOM wiring). Kept import-free so it
// can be unit-tested under `node --test` without a DOM or React renderer.
//
// The watchdog both DIAGNOSES (beacon) and RECOVERS. The terminal-payment
// poll has been observed to stop making progress while a payment is pending
// -- the backend finalizes in ~10s but the FE spinner hangs for minutes and
// only an app re-init clears it. Every candidate cause (React Query paused by
// the offline gate, a fetch wedged in the axios refresh interceptor, the
// interval ceasing to schedule) shares that one symptom, so recovery is
// driven off the symptom, not the cause:
//
//   onset (no progress for STALL_MS, foreground)  -> SOFT recover
//                                                    (force-online + reset query)
//   still stuck HARD_RECOVER_AFTER_MS after soft   -> HARD recover (reload, once)
//   real poll progress OR session final            -> episode ends
//
// Recovery is gated on real progress (dataUpdatedAt advancing past the onset
// anchor), and onset is measured from visibility-discounted time so a
// backgrounded tab can't trigger a false stall.

export const STALL_MS = 10_000
export const HEARTBEAT_MS = 30_000
export const MAX_BEACONS_PER_EPISODE = 8
// How long after the (cheap) soft recover, with still no progress, before we
// escalate to a one-shot reload.
export const HARD_RECOVER_AFTER_MS = 15_000

export interface WatchdogTickInput {
  now: number
  /** Tab hidden right now. We never evaluate while hidden. */
  hidden: boolean
  /** Session is pending / not yet final. */
  inProgress: boolean
  /** Query's last successful-fetch time; 0 before the first poll completes. */
  dataUpdatedAt: number
  /** When watching began (so a never-completing first poll still counts). */
  startedAt: number
  /** Last hidden->visible transition (or watch start); discounts background time. */
  becameVisibleAt: number
  // Carried episode state:
  stalledSince: number | null
  /** dataUpdatedAt captured at stall onset; recovery requires real progress past it. */
  stallAnchorUpdatedAt: number
  lastBeaconAt: number
  beaconCount: number
  /** When the soft recover was issued for this episode (null if not yet). */
  softRecoverAt: number | null
  /** Whether a hard recover (reload) has already fired this session. */
  hardRecoverDone: boolean
}

export type WatchdogBeacon =
  | { kind: 'stall'; sinceProgressMs: number; stalledMs: number }
  | { kind: 'recovered'; outcome: 'polling' | 'final'; stalledMs: number }

/** Side effect the hook must perform this tick. */
export type WatchdogAction = 'none' | 'soft' | 'hard'

export interface WatchdogTickResult {
  beacon: WatchdogBeacon | null
  action: WatchdogAction
  stalledSince: number | null
  stallAnchorUpdatedAt: number
  lastBeaconAt: number
  beaconCount: number
  softRecoverAt: number | null
  hardRecoverDone: boolean
}

export function evaluateWatchdogTick(i: WatchdogTickInput): WatchdogTickResult {
  const carry = (over: Partial<WatchdogTickResult> = {}): WatchdogTickResult => ({
    beacon: null,
    action: 'none',
    stalledSince: i.stalledSince,
    stallAnchorUpdatedAt: i.stallAnchorUpdatedAt,
    lastBeaconAt: i.lastBeaconAt,
    beaconCount: i.beaconCount,
    softRecoverAt: i.softRecoverAt,
    hardRecoverDone: i.hardRecoverDone,
    ...over,
  })

  // Never evaluate while hidden (data frozen, cashier not watching).
  if (i.hidden) return carry()

  // True time since last successful poll (or watch start), for the payload.
  const sinceRealProgress = i.now - Math.max(i.dataUpdatedAt, i.startedAt)

  // Session finished -> close any open episode.
  if (!i.inProgress) {
    if (i.stalledSince !== null) {
      return carry({
        beacon: { kind: 'recovered', outcome: 'final', stalledMs: i.now - i.stalledSince },
        stalledSince: null,
        stallAnchorUpdatedAt: 0,
        softRecoverAt: null,
      })
    }
    return carry()
  }

  // Already in a stall episode.
  if (i.stalledSince !== null) {
    // Recovery: a poll actually completed (progress past the onset anchor).
    if (i.dataUpdatedAt > i.stallAnchorUpdatedAt) {
      return carry({
        beacon: { kind: 'recovered', outcome: 'polling', stalledMs: i.now - i.stalledSince },
        stalledSince: null,
        stallAnchorUpdatedAt: 0,
        softRecoverAt: null,
      })
    }
    // Soft recover didn't take in time -> escalate to a one-shot reload.
    if (
      i.softRecoverAt !== null &&
      i.now - i.softRecoverAt >= HARD_RECOVER_AFTER_MS &&
      !i.hardRecoverDone
    ) {
      return carry({ action: 'hard', hardRecoverDone: true })
    }
    // Otherwise keep heartbeating (throttled + capped).
    const due = i.now - i.lastBeaconAt >= HEARTBEAT_MS
    if (due && i.beaconCount < MAX_BEACONS_PER_EPISODE) {
      return carry({
        beacon: { kind: 'stall', sinceProgressMs: sinceRealProgress, stalledMs: i.now - i.stalledSince },
        lastBeaconAt: i.now,
        beaconCount: i.beaconCount + 1,
      })
    }
    return carry()
  }

  // Not yet stalled: detect onset on visibility-discounted time.
  const onsetBaseline = Math.max(i.dataUpdatedAt, i.startedAt, i.becameVisibleAt)
  if (i.now - onsetBaseline <= STALL_MS) return carry()

  // Onset: open episode, beacon the cause, and issue the cheap soft recover.
  return carry({
    beacon: { kind: 'stall', sinceProgressMs: sinceRealProgress, stalledMs: 0 },
    action: 'soft',
    stalledSince: i.now,
    stallAnchorUpdatedAt: i.dataUpdatedAt,
    lastBeaconAt: i.now,
    beaconCount: 1,
    softRecoverAt: i.now,
  })
}
